
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { add } from 'lodash';
import { DateTime } from 'luxon';
import { ValidationObserver } from 'vee-validate';

import { sharedState } from '../../../../../framework/state';
import SelectField from '../../../../../framework/fields/SelectField.vue';
import SwitchField from '../../../../../framework/fields/SwitchField.vue';
import TextField from '../../../../../framework/fields/TextField.vue';
import MonthField from '../../../../../framework/fields/MonthField.vue';

import { ApplicationResource } from '../../../../../../common/application/enumeration/ApplicationResource';
import { VehicleContract } from '../../../../../../common/application/model/VehicleContract';
import { getCustomerGroupCustomers } from '../../../../client/customer_client';
import { moveVehicleContract } from '../../../../client/vehicle_contract_client';
import { convertLocalDateToUtc, formatCurrency, formatToFinnishDate } from '../../../../service/helper_service';
import { getVehicleCostMonthlyRows } from '../../../../client/vehicle_cost_monthly_row_client';
import { ExtendedVehicleCostMonthlyRow } from '../../../../../../common/application/model/VehicleCostMonthlyRow';
import { dateToDbDateString, dateToDbMonthDateString } from '../../../../../../common/framework/util/convert';
import { P_PAGE_SIZE } from '../../../../../../common/framework/constants';

@Component({
    components: {
        SelectField,
        TextField,
        MonthField,
        ValidationObserver,
        SwitchField,
    },
})
export default class MoveVehicleContractWizard extends Vue {
    readonly resourceType = ApplicationResource.CUSTOMER;

    @Prop(Object) readonly selectedContract!: VehicleContract;

    // State
    shared = sharedState;
    local = {
        activeStep: 0,
        ableToMove: false,
        error: '',
        row: {
            newCustomerId: '',
            transferBudget: false,
            addBalanceInvoice: true,
            transferDate: dateToDbDateString(DateTime.now().endOf('month').plus({ days: 1 }).toJSDate()),
        },
        customerOptions: [] as { id: string | undefined; label: string }[],
        vehicleMonthlyCost: { invoiced: 0, paid: 0, balance: 0 } as {
            invoiced: number;
            paid: number;
            balance: number;
        },
        loading: true,
    };

    // Functions
    async mounted() {
        this.local.loading = true;
        const data = await getCustomerGroupCustomers(this.selectedContract.customerId);

        if (data.length > 0) {
            this.local.ableToMove = true;
            this.local.customerOptions = data;
        } else {
            this.local.error = this.$t('error.noCompanyToMoveContract').toString();
        }

        this.local.loading = false;
    }

    prevStep() {
        const tmp = this.local.activeStep - 1;
        this.local.activeStep = tmp >= 0 ? tmp : 0;
    }

    nextStep() {
        this.local.activeStep += 1;
    }

    @Watch('local.activeStep')
    async getSummary(step: number) {
        if (step === 1) {
            this.local.loading = true;
            const params = new Map<string, string>([
                ['vehicleContractId', this.selectedContract.id],
                [
                    'month',
                    `${dateToDbMonthDateString(
                        DateTime.fromFormat(this.local.row.transferDate, 'yyyy-MM-dd').toJSDate(),
                    )}`,
                ],
                [P_PAGE_SIZE, '1000'],
            ]);
            const monthlyRows = await getVehicleCostMonthlyRows(params);
            this.calculateMonthlyCosts(monthlyRows.vehicleCostMonthlyRows);
            this.local.loading = false;
        }
    }

    @Watch('local.row.addBalanceInvoice')
    addBalanceInvoiceChange(newValue: boolean) {
        this.local.row.transferBudget = !newValue;
    }

    @Watch('local.row.transferBudget')
    transferBudgetChange(newValue: boolean) {
        this.local.row.addBalanceInvoice = !newValue;
    }

    get maxDate(): Date {
        if (!this.selectedContract.smartFleetContractEnd) {
            return DateTime.now().toJSDate();
        }
        return DateTime.fromJSDate(new Date(this.selectedContract.smartFleetContractEnd))
            .minus({ months: 1 })
            .startOf('day')
            .toJSDate();
    }

    get minDate(): Date {
        return DateTime.now().endOf('month').plus({ days: 1 }).startOf('day').toJSDate();
    }

    private calculateMonthlyCosts(vehicleMonthlyCosts: ExtendedVehicleCostMonthlyRow[]) {
        this.local.vehicleMonthlyCost = vehicleMonthlyCosts.reduce(
            (costs, currentMonthlyCost) => {
                costs.paid = currentMonthlyCost.paid ? add(currentMonthlyCost.paid, costs.paid) : costs.paid;
                costs.invoiced = currentMonthlyCost.invoiced
                    ? add(currentMonthlyCost.invoiced, costs.invoiced)
                    : costs.invoiced;
                costs.balance = add(currentMonthlyCost.balance, costs.balance);
                return costs;
            },
            { paid: 0, invoiced: 0, balance: 0 },
        );
    }

    confirmMoveContract() {
        this.$buefy.dialog.confirm({
            title: this.$t('title.confirmMoveContract').toString(),
            message: this.$t('message.confirmMoveContractMessage').toString(),
            cancelText: this.$t('button.cancel').toString(),
            confirmText: this.$t('button.ok').toString(),
            type: 'is-success',
            onConfirm: async () => this.moveContract(),
        });
    }

    async moveContract() {
        const { contractMoved } = await moveVehicleContract({
            ...this.local.row,
            transferDate: convertLocalDateToUtc(new Date(this.local.row.transferDate)),
            contractId: this.selectedContract.id,
            userId: this.shared.context.userId,
        });
        if (contractMoved) {
            this.$emit('contractMoved');
        }
    }

    formatDate(date: Date | undefined): string {
        if (!date) {
            return '';
        }

        return formatToFinnishDate(new Date(date).toISOString());
    }

    getCustomerName(customerId: string): string {
        const customer = this.local.customerOptions.filter((c) => c.id === customerId)[0];
        return customer ? `${customer.label} (${customer.id})` : '';
    }

    formatCurrencyValue(value: number | null | undefined) {
        return formatCurrency(value);
    }
}
